import React, { useState } from 'react';
import classNames from 'classnames';
import Button from '@mui/material/Button';
import dateFormat from 'date-fns/format';
import SendIcon from '@mui/icons-material/Send';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
    Editor,
    EditorState,
    CompositeDecorator,
    ContentState,
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import { resetLastActiveTime } from '../initStore';
import { logEvent } from '../analytics';
import { format, setTextInputEnabled } from '../helpers';
import { Colors } from '../colors';
import { InputMode, useConfig } from '../hooks';
import generalConfig from '../resources/general.json';
import { AmpFlagSet } from '../amplitude';

import styles from './MessageProvider.module.css';
import { ApplicationModalPropsType } from '../jsxElements';

export interface MessageProviderAttachmentData {
    removeBubble: boolean;
    hideAvatar: boolean;
    noMargin: boolean;
    providerName: string;
    providerId: string;
    departmentId: string;
    subject: string;
    body: string;
    flow: string;
    intent: string;
}

type MessageProviderArgs = {
    data: MessageProviderAttachmentData;
    disableButtons: boolean;
    featureFlags: AmpFlagSet;
    sendMessage: (message: string) => void;
    sendPostBack: (value: any) => void;
    setInputMode: (inputMode: InputMode) => void;
    setModalProperties:
        | ((modalProperties: ApplicationModalPropsType) => void)
        | undefined;
};

export const MessageProvider: React.FC<MessageProviderArgs> = ({
    data,
    disableButtons,
    featureFlags,
    sendPostBack,
    setInputMode,
    setModalProperties,
}: MessageProviderArgs) => {
    const { tier } = useConfig();

    const [discarded, setDiscarded] = useState(false);

    const {
        providerName,
        providerId,
        departmentId,
        subject,
        body,
        flow,
        intent,
    } = data;
    const [messageText, setMessageText] = useState(body || '');

    const onTextareaChange = (editorState: any) => {
        resetLastActiveTime();
        const contentState = editorState.getCurrentContent();
        const text = contentState.getPlainText();
        setMessageText(text);
        setEditorState(editorState);
    };

    const editor = React.useRef(null);
    const focusEditor = () => {
        (editor as any).current.focus();
    };

    const handleOverTextSpan = (props: any) => {
        return (
            <span {...props} style={{ color: '#CF2020' }}>
                {props.children}
            </span>
        );
    };

    const handleStrategy = (
        contentBlock: any,
        callback: any,
        _contentState: any
    ) => {
        const text = contentBlock.getText();
        if (text.length > 250) {
            callback(250, text.length);
        }
    };

    const compositeDecorator = new CompositeDecorator([
        {
            strategy: handleStrategy,
            component: handleOverTextSpan,
        },
    ]);

    const contentState = ContentState.createFromText(body || '');
    const [editorState, setEditorState] = React.useState(() =>
        EditorState.createWithContent(contentState, compositeDecorator)
    );

    const handleCancelMessage = () => {
        logEvent('Message Provider - Close', {
            flow,
            intent,
            subject,
            provider: providerId,
            department: departmentId,
        });
        setModalProperties &&
            setModalProperties({
                isOpen: true,
                discardAction: () => {
                    logEvent('Message Provider - Discard', {
                        flow,
                        intent,
                        subject,
                        provider: providerId,
                        department: departmentId,
                    });
                    setInputMode('enabled');
                    setTextInputEnabled(featureFlags, tier, 'enabled');
                    sendPostBack({
                        displayText:
                            generalConfig.providence.messageProviderDiscard,
                        text: '/DIRECT-cancel-message-provider',
                    });
                    setDiscarded(true);
                },
                backAction: () => {
                    logEvent('Message Provider - Go Back', {
                        flow,
                        intent,
                        subject,
                        provider: providerId,
                        department: departmentId,
                    });
                },
            });
    };

    const sendProviderMessage = (subject: string) => {
        const now = new Date();
        const timestamp = dateFormat(now, "yyyy-MM-dd'T'HH:mm:ssxxx");
        logEvent('Message Provider - Send Selected', {
            flow,
            intent,
            timestamp,
            provider: providerId,
            department: departmentId,
            msg_characters: messageText.length,
        });
        setInputMode('enabled');
        setTextInputEnabled(featureFlags, tier, 'enabled');
        const messageAsJson = { mychart_message: messageText, subject };
        sendPostBack(
            `/DIRECT-send-message-provider${JSON.stringify(messageAsJson)}`
        );
    };

    const messageHeaderText = disableButtons
        ? generalConfig.providence.messageProviderSent
        : generalConfig.providence.messageProviderNew;

    let charactersRemainingText = '';
    let charRemainingClass = '';
    if (messageText.length > 250) {
        const charactersOver = messageText.length - 250;
        if (charactersOver === 1) {
            charactersRemainingText = format(
                generalConfig.providence.messageProviderCharOver,
                { charactersOver }
            );
        } else {
            charactersRemainingText = format(
                generalConfig.providence.messageProviderCharsOver,
                { charactersOver }
            );
        }
        charRemainingClass = styles.messageCharOver;
    } else if (messageText.length >= 230) {
        const charactersRemaining = 250 - messageText.length;
        if (charactersRemaining === 1) {
            charactersRemainingText = format(
                generalConfig.providence.messageProviderCharRemaining,
                { charactersRemaining }
            );
        } else {
            charactersRemainingText = format(
                generalConfig.providence.messageProviderCharsRemaining,
                { charactersRemaining }
            );
        }
        charRemainingClass = styles.messageCharAlmostOver;
    } else {
        const charactersRemaining = 250 - messageText.length;
        charactersRemainingText = format(
            generalConfig.providence.messageProviderCharsRemaining,
            { charactersRemaining }
        );
        charRemainingClass = styles.messageCharRemaining;
    }

    const sendDisabled = messageText.length === 0 || messageText.length > 250;

    if (discarded) {
        return null;
    }

    const blueBorder = disableButtons
        ? styles.messageContainerBlueBorder
        : null;
    const sendDisabledClass = sendDisabled
        ? styles.messageSendIconDisabled
        : null;

    const buttonOverride = {
        '&.Mui-disabled': {
            backgroundColor: 'rgba(0, 51, 142, 0.4)',
            color: 'white',
        },
        ':hover': {
            backgroundColor: Colors.PrimaryLight,
            boxShadow: 'none',
        },
    };

    return (
        <div className={classNames(styles.messageContainer, blueBorder)}>
            <div className={styles.messageHeaderBar}>
                <span className={styles.messageHeader}>
                    {messageHeaderText}
                </span>
                {!disableButtons && (
                    <IconButton
                        className={styles.messageCloseButton}
                        onClick={handleCancelMessage}
                        size="large"
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </div>
            {!disableButtons && (
                <div className={styles.messageRecipientsBar}>
                    <div className={styles.messageRecipientsRow}>
                        <span className={styles.messageToRecipientLabel}>
                            To
                        </span>
                        <span className={styles.messageToRecipient}>
                            {providerName}
                        </span>
                    </div>
                    <div className={styles.messageRecipientsRow}>
                        <span className={styles.messageToRecipientLabel}>
                            Subject
                        </span>
                        <span className={styles.messageToRecipient}>
                            {subject}
                        </span>
                    </div>
                </div>
            )}
            {!disableButtons && (
                <>
                    <div className={styles.messageTextInputWrapper}>
                        <div
                            className={styles.messageTextInput}
                            onClick={focusEditor}
                        >
                            <Editor
                                ref={editor}
                                editorState={editorState}
                                onChange={onTextareaChange}
                                stripPastedStyles={true}
                                placeholder={
                                    generalConfig.providence
                                        .messageProviderPlaceholder
                                }
                                readOnly={disableButtons}
                            />
                        </div>
                    </div>
                    <div className={charRemainingClass}>
                        <span>{charactersRemainingText}</span>
                    </div>
                    <div className={styles.messageActionBar}>
                        <Button
                            variant="contained"
                            className={styles.messageSendButton}
                            onClick={() => sendProviderMessage(subject)}
                            sx={buttonOverride}
                            disabled={sendDisabled}
                        >
                            Send
                            <SendIcon
                                className={classNames(
                                    styles.messageSendIcon,
                                    sendDisabledClass
                                )}
                            />
                        </Button>
                    </div>
                </>
            )}
            {disableButtons && (
                <>
                    <div className={styles.messageSentTextContainer}>
                        {messageText}
                    </div>
                </>
            )}
        </div>
    );
};

export default MessageProvider;
